import React, { useContext, useReducer, useState } from 'react'
import Link from 'next/link'

import {
  headerContainer,
  headerLogoContainer,
  headerMenuTriggerContainer,
  headerMenuContainer,
  headerSearchButtonContainer,
  headerMenuButtonContainer,
  headerDesktopButtonsContainer,
  headerDesktopSeparator,
  headerFirstItemContainer,
  headerDesktopDropdownButtonContainer,
  iconStyle,
} from './Header.css'
import { BellIcon, Icon } from 'public/assets/v2/images/icons'
import { BrowserContext, UserContext } from '../../../context'
import {
  CreatorlandLogo,
  ButtonComponent,
  IconButton,
  DropdownMenu,
} from '@/components/commons'
import { useUrlWithExistingParams } from '@/shared/hooks/url'
import { useRouter } from 'next/router'
import { SearchButton } from '@/components/search/SearchButton'
import { useUser } from 'containers/user/hooks/useUser'
import { cssClass } from '@/shared/utils'
import Image from 'next/image'
import { useSubscription } from 'containers/profile/hooks/useProfile'
import { Notifications } from 'containers/notifications'
import { useNotifications } from 'containers/notifications/hooks/notifications'

interface HeaderProps {
  loginUrl?: string
  signupUrl?: string
  isAuthenticated?: boolean
}

export const Header: React.FC<HeaderProps> = ({ loginUrl, signupUrl }) => {
  const { logout } = useContext(UserContext)
  const { isAuth } = useUser()
  const { isSubscribed } = useSubscription()
  const [isNotificationsOpen, toggleNotifications] = useState(false)
  const { newNotifications } = useNotifications()

  const [isMenuVisible, toggleMenu] = useReducer((state) => !state, false)

  const openNotifications = () => {
    toggleNotifications(true)
  }

  return (
    <>
      <Notifications
        isOpen={isNotificationsOpen}
        onClose={() => toggleNotifications(false)}
      />

      <div
        className={cssClass([headerContainer, 'z-11000000'])}
        data-authenticated={isAuth}
      >
        <div className={headerLogoContainer}>
          <Link href="/">
            <CreatorlandLogo />
          </Link>
        </div>

        {isAuth && (
          <div className={headerFirstItemContainer}>
            <div className="flex flex-row gap-6 items-center relative">
              <BellIcon onClick={openNotifications} />
              {newNotifications > 0 && (
                <div
                  style={{
                    width: 8,
                    height: 8,
                  }}
                  className='bg-danger rounded-20 absolute -top-2 -right-4'
                />
              )}
            </div>
          </div>
        )}

        <div>
          <div className="flex flex-row gap-6 items-center">
            <SearchButton className={headerSearchButtonContainer} />
          </div>
        </div>

        <div className={headerMenuTriggerContainer}>
          <IconButton icon="MenuIcon" onClick={toggleMenu} />
        </div>

        <div className={headerDesktopSeparator} />

        <div className={headerDesktopDropdownButtonContainer}>
          <DropdownLoginButton
            loginUrl={loginUrl}
            signupUrl={signupUrl}
            isAuthenticated={isAuth}
          />
        </div>

        <div className={headerDesktopButtonsContainer}>
          <SignInButton url={loginUrl} isAuthenticated={isAuth} />
          <SignUpButton url={signupUrl} isAuthenticated={isAuth} />
        </div>

        <div
          className={`${headerMenuContainer} border-b-1 border-b-solid border-b-secondary`}
          data-visible={isMenuVisible}
        >
          {!isAuth && (
            <div
              className={`${headerMenuButtonContainer} flex flex-row items-center gap-16 justify-center`}
              onClick={toggleMenu}
            >
              <SignUpButton url={signupUrl} isAuthenticated={isAuth} />
              &nbsp;or&nbsp;
              <SignInButton url={loginUrl} isAuthenticated={isAuth} />
            </div>
          )}

          {isAuth && (
            <>
              <div className={headerMenuButtonContainer} onClick={toggleMenu}>
                <Link href="/settings">
                  <Icon.SettingsIcon className={iconStyle} /> Settings
                </Link>
              </div>
            </>
          )}


          <div className={headerMenuButtonContainer} onClick={toggleMenu}>
            <Link href="/jobs" className="flex items-center gap-6">
              <Icon.JobsIcon />
              Jobs
            </Link>
          </div>

          <div className={headerMenuButtonContainer} onClick={toggleMenu}>
            <Link
              href="https://business.creatorland.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-6"
            >
              <Icon.FolderIcon className={iconStyle} />
              Creatorland for Business
            </Link>
          </div>
          
          <div className={headerMenuButtonContainer} onClick={toggleMenu}>
            <Link target="_blank" className="flex items-center gap-6" href="https://newsletter.creatorland.com/c/university">
              <Icon.University className={iconStyle} />
              Creatorland University
            </Link>
          </div>

          <div className={headerMenuButtonContainer} onClick={toggleMenu}>
            {!isAuth || !isSubscribed ? (
              <Link href="/checkout" className="flex items-center gap-6">
                <Image
                  alt=""
                  width={16}
                  height={16}
                  src={Icon.InsidersLifeTimeBadgePNG}
                />
                Get Premium
              </Link>
            ) : (
              <Link href="/insiderrewards" className="flex items-center gap-6">
                <Image
                  alt=""
                  width={16}
                  height={16}
                  src={Icon.InsidersLifeTimeBadgePNG}
                />
                Insider Rewards
              </Link>
            )}
          </div>

          <div className={headerMenuButtonContainer} onClick={toggleMenu}>
            <Link href="https://chatgpt.com/g/g-67361d0e1a888190911b1688ec990a2d-creatorland-co-creator" target='_blank' className="flex items-center gap-6">
              <Icon.CoCreatorIcon />
              Co-Creator
            </Link>
          </div>

          {/* <div className={headerMenuButtonContainer} onClick={toggleMenu}>
            <Link href="/discord">
              <Icon.CommentIcon className={iconStyle} /> Join the Discord
            </Link>
          </div> */}

          {/* <div className={headerMenuButtonContainer} onClick={toggleMenu}>
            <Link href="https://help.creatorland.com/hc/en-us">
              <Icon.InfoCircle className={iconStyle} /> Knowledge Base
            </Link>
          </div> */}

          <div className={headerMenuButtonContainer} onClick={toggleMenu}>
            <Link href="mailto:support@creatorland.com" className="flex items-center gap-6" target='_blank'>
              <Icon.SupportIcon className={iconStyle} />
               Support
            </Link>
          </div>

          {/* <div className={headerMenuButtonContainer} onClick={toggleMenu}>
            <Link href="/legal">
              <Icon.ShieldSecurity className={iconStyle} /> Legal
            </Link>
          </div> */}

          {isAuth && (
            <div className={headerMenuButtonContainer} onClick={toggleMenu}>
              <Link href="#" onClick={() => logout()}>
                <Icon.LogoutIcon className={iconStyle} /> Logout
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

interface SignInSignupProps {
  url?: string
  isAuthenticated?: boolean
}

const SignInButton: React.FC<SignInSignupProps> = ({
  url,
  isAuthenticated,
}) => {
  const { isMobile } = useContext(BrowserContext)
  const { asPath } = useRouter()
  const urlCallback = asPath
  const { urlWithParams } = useUrlWithExistingParams(url || '/login', {
    urlCallback,
  })

  if (isAuthenticated) return <></>
  return (
    <Link href={urlWithParams}>
      <ButtonComponent
        variation="tertiary"
        label="Sign In"
        size={isMobile ? 'xSmall' : 'regular'}
      />
    </Link>
  )
}

const SignUpButton: React.FC<SignInSignupProps> = ({
  url,
  isAuthenticated,
}) => {
  const { isMobile } = useContext(BrowserContext)
  const { urlWithParams } = useUrlWithExistingParams(url || '/signup')

  if (isAuthenticated) return <></>
  return (
    <Link href={urlWithParams}>
      <ButtonComponent
        label="Join Now"
        size={isMobile ? 'xSmall' : 'regular'}
      />
    </Link>
  )
}

const DropdownLoginButton = ({
  loginUrl,
  signupUrl,
  isAuthenticated,
}: HeaderProps) => {
  const lUrl = loginUrl || '/login'
  const sUrl = signupUrl || '/signup'
  if (isAuthenticated) return <></>
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <ButtonComponent label="Join Now" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <DropdownMenu.Item>
          <Link href={sUrl}>Sign Up</Link>
        </DropdownMenu.Item>
        <DropdownMenu.Item>
          <Link href={lUrl}>Login</Link>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
